import React, { useState } from 'react'
import styled from '@emotion/styled'
import { YSpacing } from '@components/common'
import { AutocompleteInput } from '@containers/common/form'
import { Checkbox } from '@components/common/form'
import { Account, GOClientLocation, GOClientSettings } from '@types'

export interface SearchClientSettingsParams {
  clientIds: string
  active: boolean
}

interface ClientLocationSelectorProps {
  onSelect: (
    account: Account,
    setting: GOClientSettings,
    location: GOClientLocation,
  ) => void
  loadAccounts: (val: { search: string }) => Promise<Account[]>
  searchClientSettings: (
    params: SearchClientSettingsParams,
  ) => Promise<GOClientSettings[]>
  initialAccount: Account | null
  initialClientSetting: GOClientSettings | null
  withinBudget: boolean | null
  onChangeWithinBudget: (withinBudget: boolean) => void
}

export const ClientLocationSelector: React.FC<ClientLocationSelectorProps> = ({
  onSelect,
  loadAccounts,
  searchClientSettings,
  initialAccount,
  initialClientSetting,
  withinBudget,
  onChangeWithinBudget,
}) => {
  const [account, setAccount] = useState<Account | null>(initialAccount)
  const [clientSettings, setClientSettings] = useState<GOClientSettings[]>(
    initialClientSetting ? [initialClientSetting] : [],
  )
  const [selectedClientSetting, setSelectedClientSetting] =
    useState<GOClientSettings | null>(initialClientSetting)

  const handleAccountSelect = async (selectedAccount: Account) => {
    setAccount(selectedAccount)
    setClientSettings([])
    setSelectedClientSetting(null)
    const clientSettings = await searchClientSettings({
      clientIds: selectedAccount.id,
      active: true,
    })
    setClientSettings(clientSettings)
  }

  const handleSelectClientSettings = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const setting = clientSettings.find((o) => o.id === e.target.value)
    if (setting && account) {
      setSelectedClientSetting(setting)
      onSelect(account, setting, setting.location)
    }
  }

  return (
    <Container>
      <FormLabel>Select Client</FormLabel>
      <FormSection>
        <AutocompleteInput
          width="100%"
          loaderFunction={(search: { search: string }) =>
            loadAccounts({ ...search })
          }
          onSelect={handleAccountSelect}
          value={account?.name || initialAccount?.name}
        />
      </FormSection>
      <YSpacing height="10px" />
      {account && (
        <FormSection>
          <FormLabel>Select Location</FormLabel>
          {clientSettings.length > 0 ? (
            <select
              style={{ width: '100%', padding: '8px', borderRadius: '4px' }}
              onChange={handleSelectClientSettings}
              value={
                selectedClientSetting?.id || initialClientSetting?.id || ''
              }
            >
              <option value="">- Select Client Location -</option>
              {clientSettings.map((setting) => (
                <option key={setting.id} value={setting.id}>
                  {setting.name} -{' '}
                  {`${setting.location.line1} ${
                    setting.location.line2 || ''
                  }`.trim()}
                  , {setting.location.city}, {setting.location.state}
                </option>
              ))}
            </select>
          ) : (
            <p>No active group order locations found for this client</p>
          )}
        </FormSection>
      )}
      {account && (
        <FormSection>
          <Checkbox
            label="Only show menus within client budget"
            labelStyle="uppercase"
            checked={withinBudget}
            onChange={() => onChangeWithinBudget(!withinBudget)}
          />
        </FormSection>
      )}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 0px;
  width: 100%;
`
const FormSection = styled.div`
  margin-bottom: 10px;
  width: 100%;
`

const FormLabel = styled.label`
  display: block;
  font-weight: bold;
  margin-bottom: 8px;
`

export default ClientLocationSelector
